<script setup lang="ts">
const statsSection = ref<HTMLElement | null>(null);
const animated = ref(false);
const count15 = ref(0);
const count100 = ref(0);
const count98 = ref(0);
const count360 = ref(0);

const colorMode = useColorMode();

function animateCount(target: number, setter: (v: number) => void) {
  const duration = 1500;
  const steps = 30;
  const stepTime = duration / steps;
  let current = 0;
  const increment = target / steps;
  const timer = setInterval(() => {
    current += increment;
    if (current >= target) {
      setter(target);
      clearInterval(timer);
    } else {
      setter(Math.round(current));
    }
  }, stepTime);
}

onMounted(() => {
  if (statsSection.value) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animated.value) {
            animated.value = true;
            animateCount(15, (v) => (count15.value = v));
            animateCount(100, (v) => (count100.value = v));
            animateCount(98, (v) => (count98.value = v));
            animateCount(360, (v) => (count360.value = v));
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(statsSection.value);
  }
});
</script>

<template>
  <div ref="statsSection" class="flex justify-center">
    <div
      class="stats-card flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-6 lg:gap-10"
      :class="colorMode.preference === 'dark' ? 'stats-card-dark' : 'stats-card-light'"
    >
      <!-- Stat 1 -->
      <div class="flex flex-col items-center flex-1">
        <span class="stat-number font-avant-demi" :class="colorMode.preference === 'dark' ? 'stat-number-dark' : 'stat-number-light'">{{ count15 }}+</span>
        <span class="stat-label font-avant" :class="colorMode.preference === 'dark' ? 'stat-label-dark' : 'stat-label-light'">Years of experience</span>
      </div>
      <!-- Stat 2 -->
      <div class="flex flex-col items-center flex-1">
        <span class="stat-number font-avant-demi" :class="colorMode.preference === 'dark' ? 'stat-number-dark' : 'stat-number-light'">{{ count100 }}+</span>
        <span class="stat-label font-avant" :class="colorMode.preference === 'dark' ? 'stat-label-dark' : 'stat-label-light'">Brands Transformed</span>
      </div>
      <!-- Stat 3 -->
      <div class="flex flex-col items-center flex-1">
        <span class="stat-number font-avant-demi" :class="colorMode.preference === 'dark' ? 'stat-number-dark' : 'stat-number-light'">{{ count98 }}%</span>
        <span class="stat-label font-avant" :class="colorMode.preference === 'dark' ? 'stat-label-dark' : 'stat-label-light'">Client Satisfaction</span>
      </div>
      <!-- Stat 4 -->
      <div class="flex flex-col items-center flex-1">
        <span class="stat-number font-avant-demi" :class="colorMode.preference === 'dark' ? 'stat-number-dark' : 'stat-number-light'">{{ count360 }}°</span>
        <span class="stat-label font-avant" :class="colorMode.preference === 'dark' ? 'stat-label-dark' : 'stat-label-light'">Brand Alignment</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stats-card {
  width: 944px;
  height: 123px;
  padding: 28px 52px;
  border-radius: 9px;
}

@media (max-width: 1023px) {
  .stats-card {
    width: 100%;
    height: auto;
    padding: 20px 16px;
  }
}

.stats-card-dark {
  border: 1px solid #3D1B60;
  background: linear-gradient(92deg, rgba(1, 3, 20, 0.40) 6.22%, rgba(43, 1, 82, 0.40) 93.78%);
}

.stats-card-light {
  border: 1px solid rgba(61, 27, 96, 0.2);
  background: linear-gradient(92deg, #FFF 6.22%, #F2F2F2 93.78%);
}

.stat-number {
  align-self: stretch;
  text-align: center;
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.stat-number-dark {
  color: #FFF;
}

.stat-number-light {
  color: #550698;
}

.stat-label {
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
  white-space: nowrap;
  opacity: 0.6;
}

.stat-label-dark {
  color: #FFF;
}

.stat-label-light {
  color: #000;
}
</style>
